/**
 * Text of the job lifecycle, Traditional Chinese.
 *
 *   User Mode   userFlowMessages: a handful of short status lines. Never
 *               「付款成功」, never an order id, a mode, a fingerprint or a gate.
 *   Developer   flowDevLines(): 付款模式, Quote 狀態, Payment, Payment
 *               verification, ids, fingerprints, quoted amount, 翻譯 gate.
 */

import type { PaymentMode } from '../payment/types';
import { formatTwd } from '../pricing/format';
import type { GateCheckName, GateResult, JobState } from './lifecycle';

// ---------------------------------------------------------------------------
// User Mode
// ---------------------------------------------------------------------------

/** Shown between 確認價格並繼續 and the first translation step. No payment happened, so no payment word. */
export const MSG_CONFIRMING = '正在確認翻譯資訊…';
export const MSG_QUOTE_EXPIRED = '報價已過期，請重新估價。';
/** disabled mode cannot produce this; kept for the FAILED / CANCELLED payment states of future modes. */
export const MSG_PAYMENT_NOT_CONFIRMED = '尚未完成付款確認，翻譯尚未開始。';
export const MSG_SCOPE_CHANGED = '翻譯範圍已變更，請重新確認價格。';
export const MSG_RETRY = '重試';

export const USER_FLOW_MESSAGES = {
  confirming: MSG_CONFIRMING,
  quoteExpired: MSG_QUOTE_EXPIRED,
  paymentNotConfirmed: MSG_PAYMENT_NOT_CONFIRMED,
  scopeChanged: MSG_SCOPE_CHANGED,
  retry: MSG_RETRY,
  confirmButton: '確認價格並繼續',
} as const;

/** Why the gate refused, in words for the user. */
export function gateFailureMessage(gate: GateResult): string {
  switch (gate.failed) {
    case 'quote_not_expired':
      return MSG_QUOTE_EXPIRED;
    case 'document_fingerprint':
    case 'scope_fingerprint':
      return MSG_SCOPE_CHANGED;
    case 'payment_confirmed':
    case 'entitlement_active':
      return MSG_PAYMENT_NOT_CONFIRMED;
    default:
      return MSG_SCOPE_CHANGED;
  }
}

// ---------------------------------------------------------------------------
// Developer Mode
// ---------------------------------------------------------------------------

const GATE_LABEL: Record<GateCheckName, string> = {
  confirmed_quote: '已確認報價',
  quote_not_expired: '報價未過期',
  document_fingerprint: '文件指紋一致',
  scope_fingerprint: '範圍指紋一致',
  payment_confirmed: '付款狀態 CONFIRMED',
  entitlement_active: 'Entitlement 有效',
  status: 'Job 狀態允許開始',
};

const INVALIDATION_LABEL = {
  document_changed: '更換文件',
  scope_changed: '變更範圍',
  quote_expired: '報價過期',
  payment_failed: '付款失敗或取消',
  completed: '前次翻譯已完成',
  reset: '重新選擇檔案',
} as const;

function quoteStatus(s: JobState): string {
  if (s.confirmedQuote) return `confirmed（${s.confirmedQuote.confirmedAt}）`;
  if (s.quote) return 'ready（尚未確認，範圍可自由修改）';
  return 'none';
}

function paymentLine(s: JobState, mode: PaymentMode): string {
  const p = s.payment;
  if (!p) return mode === 'disabled' ? '尚未開始（disabled mode 會自動 bypass）' : '尚未開始';
  if (p.bypassed) return `bypassed（${p.paymentMode} mode，未產生實際付款，本地訂單 ${p.orderId}）`;
  return `${p.status}（${p.paymentMode} mode，訂單 ${p.orderId}）`;
}

function verificationLine(s: JobState): string {
  const p = s.payment;
  if (!p) return '尚未驗證';
  if (p.status === 'CONFIRMED') return p.authority === 'disabled-bypass' ? `confirmed (disabled mode)，${p.confirmedAt}` : `confirmed (${p.authority})，${p.confirmedAt}`;
  return `${p.status.toLowerCase()}（${p.authority}）`;
}

function entitlementLine(s: JobState): string {
  const e = s.entitlement;
  if (!e) return s.lastInvalidation && s.lastInvalidation !== 'reset' ? `無（前一個已失效：${INVALIDATION_LABEL[s.lastInvalidation]}）` : '無';
  return `${e.status}（${e.issuedBy}，${e.issuedAt} → ${e.expiresAt}）`;
}

/** Developer Mode panel (spec §16). */
export function flowDevLines(s: JobState, mode: PaymentMode): string[] {
  const cq = s.confirmedQuote;
  const shown = cq?.snapshot ?? s.quote;
  const lines = [
    `付款模式：${mode}`,
    `Job 狀態：${s.status}`,
    `Quote 狀態：${quoteStatus(s)}`,
    `Payment：${paymentLine(s, mode)}`,
    `Payment verification：${verificationLine(s)}`,
    '',
    `Quote ID：${shown?.quoteId ?? '—'}`,
    `定價版本：${shown?.pricingVersion ?? '—'}`,
    `Document fingerprint：${shown?.documentFingerprint ?? '—'}`,
    `Scope fingerprint：${shown?.scopeFingerprint ?? '—'}`,
    `Quoted amount：${shown ? formatTwd(cq ? cq.quotedAmount : shown.quotedAmount) : '—'}`,
    `報價有效期限：${shown?.expiresAt ?? '—'}`,
    `Entitlement：${entitlementLine(s)}`,
    `翻譯啟動次數：${s.translationStarts}${s.translationStarts > 1 ? '（含重試，未重新確認價格、未重新建立付款）' : ''}`,
  ];
  if (s.failure) lines.push(`最近失敗：${s.failure}`);
  lines.push('');
  const g = s.lastGate;
  if (!g) {
    lines.push('翻譯 gate：尚未評估（按下確認價格後才會檢查）');
  } else {
    lines.push(`翻譯 gate：${g.pass ? 'PASS' : 'FAIL'}`);
    for (const c of g.checks) lines.push(`  ${c.ok ? '✓' : '✗'} ${GATE_LABEL[c.name]}：${c.detail}`);
  }
  return lines;
}

/** One line for the panel's <summary>. */
export function flowDevSummary(s: JobState, mode: PaymentMode): string {
  const gate = s.lastGate ? (s.lastGate.pass ? 'gate PASS' : 'gate FAIL') : 'gate 未評估';
  return `翻譯流程（開發模式）：${s.status} · 付款模式 ${mode} · ${gate}`;
}
